// ── "Install app": telling the reader the chat CAN be installed (report 8c5f3514) ───────────────────────────────────
// The chat app was finished and nothing on the phone said so. Everything a phone needs was already being served —
// the manifest and the service worker both answer (components/Chat/ChatApp.tsx, public/chat.webmanifest,
// public/sw.js) — but Chrome on Android has stopped putting up a banner of its own and now tucks "Install app" away
// in the ⋮ menu, and an iPhone has never offered anything at all: its "Add to Home Screen" lives under the Share
// sheet and no page can open it. So the only reader who ever found out was one who already knew where to look, and
// Calem's screenshot is the chat with no way in sight to install it.
// This is the small amount of state that lets the chat's own top row carry a button for it:
// · A browser that WILL show its own install dialog says so first, by firing `beforeinstallprompt`. That event IS
//   the offer, and holding on to it is the only way to open that dialog — it cannot be summoned later out of
//   nothing. It fires early, usually before React has drawn anything, and a browser fires it once; so
//   watchInstallOffer() is called from src/index.tsx before the app mounts and the event is kept here, where
//   whichever button is drawn later can find it. Caught on a page of the system other than /chat, it is still the
//   right offer when the reader walks to /chat afterwards: it is one event per page load, not per address.
// · A browser that fires nothing — every iPhone, and a Chrome whose one offer has already been used — leaves `held`
//   empty, and install() answers false so the button can say what to tap instead (ChatPanel: InstallHow), on the
//   chat page, which the pop-up's button takes the phone to first (the note below).
// · Once Chat IS the installed app there is nothing left to install, so the button goes: runningAsApp() is true
//   inside an installed window (the manifest asks for `display: standalone`; an iPhone says so through
//   navigator.standalone instead, supporting no such query), and `appinstalled` covers the browser tab it was
//   installed FROM, which stays open and would otherwise go on offering it.
// Nothing here is particular to the chat: what it keeps is the browser's own state, one lot per page load.
import { useCallback, useEffect, useState } from 'react';

// ── The note the chat pop-up leaves for the chat page (report 96e28b88, opening #3) ──────────────────────────────
// The reporter: "Allow users with an iPhone to download a home screen chat app rather than redirect them to the
// firm's site." A phone's "Add to Home Screen" saves the page the phone is ON, and from the pop-up that page is
// whatever the reader was working in — so the icon they got opened the system itself and not Chat. The pop-up's button
// therefore sends the phone to the chat app's own address first, with a real page load (ChatPanel), and the sheet
// that says which of the phone's buttons to press has to outlive that load. This is how: one key in sessionStorage,
// which is per browser tab, read once and then forgotten.
// One key per app (report ac3c43b4): the Files app's button beside Chat's sends the phone to /files the same way, and
// the note it leaves is for the Files page alone — never opened by the chat page, nor the other way round. The Email
// app's button likewise (report 0e8dd0cb).
export type InstallApp = 'chat' | 'files' | 'email';
const howKey = (app: InstallApp): string => `mf_${app}_install_how`;

/** Ask for the install instructions to be shown once that app's own page has loaded. */
export function wantInstallHow(app: InstallApp = 'chat'): void {
  try { sessionStorage.setItem(howKey(app), '1'); } catch { /* private mode: the page simply opens without the sheet */ }
}

/** Were they asked for? Answers true once and forgets, so a later visit to that app's page in this tab opens nothing. */
export function takeInstallHow(app: InstallApp = 'chat'): boolean {
  try {
    if (!sessionStorage.getItem(howKey(app))) return false;
    sessionStorage.removeItem(howKey(app));
    return true;
  } catch { return false; }
}

/** The `beforeinstallprompt` event. Not in TypeScript's DOM library, as no standard carries it. */
type InstallEvent = Event & { prompt: () => Promise<void> };

let watching = false;
let held: InstallEvent | null = null;   // the browser's own offer, kept for the button that is drawn later
let installed = false;                  // the browser said it installed Chat during this page's life
const told = new Set<() => void>();     // the buttons drawn now, to be redrawn when that becomes true

/** Listen for the browser's offer, and for its "installed". Called from src/index.tsx BEFORE the app is mounted,
 *  because `beforeinstallprompt` can fire while React is still starting and would otherwise be gone for good. */
export function watchInstallOffer(): void {
  if (watching) return;
  watching = true;
  window.addEventListener('beforeinstallprompt', e => {
    // Without this the browser is free to put a bar of its own across the page — and, having shown it, to count the
    // offer as made and never hand it over. Held back, that dialog opens on the button instead.
    // No button has to be redrawn for this: one is offered wherever there is still something to install, and this
    // only decides which of the two things a press does.
    e.preventDefault();
    held = e as InstallEvent;
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    held = null;
    told.forEach(f => f());
  });
}

/** Is this page already the installed app rather than a page in a browser? A window opened from the home-screen
 *  icon has no browser frame round it, which is what these three display modes mean; an iPhone answers instead
 *  through navigator.standalone. */
export function runningAsApp(): boolean {
  const asked = (q: string): boolean => !!window.matchMedia?.(q).matches;
  if (asked('(display-mode: standalone)') || asked('(display-mode: fullscreen)') || asked('(display-mode: minimal-ui)')) return true;
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export type InstallOffer = {
  /** Draw the button at all: there is still something to install. */
  show: boolean;
  /** Open the browser's own install dialog. false = the browser gave this page none, so the reader has to be told
   *  what to tap on their own phone. */
  install: () => Promise<boolean>;
};

/** What an Install button needs. It is redrawn when the browser says it has installed Chat, and if the window is
 *  moved into app mode under it — the two ways there stops being anything to install. */
export function useInstallOffer(): InstallOffer {
  const [, redraw] = useState(0);
  useEffect(() => {
    const read = () => redraw(n => n + 1);
    told.add(read);
    // An installed window is standalone from its first paint, so this is for the rarer other way round.
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener('change', read);
    return () => { told.delete(read); mq?.removeEventListener('change', read); };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    const offer = held;
    if (!offer) return false;
    // A browser lets its offer be used once. Let go of it first, so a second press reaches the instructions rather
    // than a dialog that will never open again — and if the browser makes a fresh offer, that one takes its place.
    held = null;
    try {
      await offer.prompt();
    } catch {
      return false;   // the dialog would not open; the instructions are the honest answer
    }
    return true;
  }, []);

  // Read straight from the browser rather than out of state, so this is the state of this very paint; the effect
  // above is only what makes a paint happen when it changes.
  return { show: !installed && !runningAsApp(), install };
}
